import { Refresh } from "@mui/icons-material";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, useDataProvider, useNotify, useTranslate } from "react-admin";

import { INTEGRATION_CREDENTIALS_NOT_CONFIGURED, INTEGRATION_SETTINGS_INVALID } from "../constants/integrationErrors";
import {
  IntegrationConnectionCheck,
  IntegrationCredentialsInput,
  IntegrationExecution,
  IntegrationProvider,
} from "../generated/graphql";
import { useOrganizationContext } from "../hooks/useOrganizationContext";
import { space, theme } from "../theme/designTokens";
import { IntegrationExecutionsTable } from "./IntegrationExecutionsTable";
import { IntegrationImportPreview } from "./IntegrationImportPreview";

/**
 * Translation key prefixes for each provider
 */
const providerTranslations: Record<IntegrationProvider, string> = {
  [IntegrationProvider.Senso]: "integration.form.senso",
  [IntegrationProvider.Vivendi]: "integration.form.vivendi",
  [IntegrationProvider.MedifoxDan]: "integration.form.medifoxDan",
};

/**
 * Default empty credentials
 */
const defaultCredentials: IntegrationCredentialsInput = {
  serverUrl: "",
  clientId: "",
  clientSecret: "",
};

/**
 * Simple URL validator, used by all providers that require one.
 * Requires protocol (http:// or https://) for proper URL validation.
 */
const validateUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === "https:";
  } catch {
    return false;
  }
};

type IntegrationState = {
  provider: IntegrationProvider;
  credentials: IntegrationCredentialsInput;
};

/**
 * The connection test runs with the saved credentials, so only these two
 * saved values (the secret is never returned) can be compared with the form.
 */
type SavedConnection = { serverUrl: string; clientId: string };

const toSavedConnection = (credentials: IntegrationCredentialsInput): SavedConnection => ({
  serverUrl: credentials.serverUrl ?? "",
  clientId: credentials.clientId ?? "",
});

export const IntegrationSettings: React.FC = () => {
  const { organizationId } = useOrganizationContext();
  const translate = useTranslate();
  return organizationId ? (
    <OrganizationIntegrationSettings key={organizationId} organizationId={organizationId} />
  ) : (
    <Typography>{translate("integration.error.noOrganizationSelected")}</Typography>
  );
};

const OrganizationIntegrationSettings = ({ organizationId }: { organizationId: string }) => {
  const [integrationState, setIntegrationState] = useState<IntegrationState>({
    provider: IntegrationProvider.Senso,
    credentials: defaultCredentials,
  });
  const [savedProvider, setSavedProvider] = useState<IntegrationProvider | null>(null);
  const [hasClientSecret, setHasClientSecret] = useState(false);
  const hasSavedSecret = hasClientSecret && savedProvider === integrationState.provider;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [startingSync, setStartingSync] = useState(false);
  const [savedConnection, setSavedConnection] = useState<SavedConnection | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionCheck, setConnectionCheck] = useState<IntegrationConnectionCheck | null>(null);
  // Invalidates a test response that arrives after the form changed.
  const connectionTestRun = useRef(0);

  // State for execution tracking
  const [executions, setExecutions] = useState<IntegrationExecution[]>([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);

  const notify = useNotify();
  const translate = useTranslate();
  const dataProvider = useDataProvider();

  const clearConnectionCheck = useCallback(() => {
    connectionTestRun.current += 1;
    setConnectionCheck(null);
    setTestingConnection(false);
  }, []);

  /**
   * Load integration executions
   */
  const loadExecutions = useCallback(async () => {
    setExecutionsLoading(true);
    try {
      const executionsList = await dataProvider.getIntegrationExecutions(organizationId, { limit: 10, offset: 0 });
      setExecutions(executionsList || []);
    } catch (err) {
      console.error("Error loading executions:", err);
      // Optionally show notification for loading errors
      // notify("Error loading sync history", { type: "error" });
    } finally {
      setExecutionsLoading(false);
    }
  }, [dataProvider, organizationId]);

  /**
   * Load existing integration settings on component mount
   */
  useEffect(() => {
    const loadIntegrationSettings = async () => {
      try {
        const settings = await dataProvider.getIntegrationSettings(organizationId);
        if (settings) {
          setIntegrationState({
            provider: settings.provider as IntegrationProvider,
            credentials: { ...settings.credentials, clientSecret: "" },
          });
          setSavedProvider(settings.provider);
          setSavedConnection(toSavedConnection(settings.credentials));
          setHasClientSecret(settings.hasClientSecret);
        }
      } catch (err) {
        notify("integration.error.loading", { type: "error" });
        // Not showing error to user as this might be the first time setting up
      } finally {
        setLoading(false);
      }
    };

    loadIntegrationSettings();
    loadExecutions();
  }, [dataProvider, notify, loadExecutions, organizationId]);

  /**
   * A secret entered for one provider must not be reused for another.
   */
  const handleProviderChange = (e: SelectChangeEvent<string>) => {
    const provider = e.target.value as IntegrationProvider;
    setIntegrationState(prev => ({
      ...prev,
      provider,
      credentials: { ...prev.credentials, clientSecret: "" },
    }));
    setError(null);
    clearConnectionCheck();
  };

  /**
   * Generic change handler: updates the right field in credentials
   */
  const handleInputChange = (field: keyof IntegrationCredentialsInput) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setIntegrationState(prev => ({
      ...prev,
      credentials: { ...prev.credentials, [field]: e.target.value },
    }));
    clearConnectionCheck();
  };

  /**
   * Validation logic:
   * - All required fields must be filled
   * - serverUrl must be a valid URL
   */
  const validateCredentials = (): string | null => {
    const { serverUrl, clientId, clientSecret } = integrationState.credentials;

    if (!serverUrl || !clientId || (!clientSecret && !hasSavedSecret)) {
      return translate("integration.validation.allFieldsRequired");
    }

    if (!validateUrl(serverUrl)) {
      return translate("integration.validation.invalidUrl");
    }

    return null;
  };

  /**
   * Sync integration data
   */
  const handleSync = async () => {
    setStartingSync(true);
    setError(null);

    try {
      const execution = await dataProvider.syncIntegrationData(organizationId);
      setExecutions(prev => [execution, ...prev]);
      notify("integration.success.syncStarted", { type: "success" });
    } catch (err) {
      const errorMessage = (err as Error)?.message;
      const msg =
        errorMessage === INTEGRATION_CREDENTIALS_NOT_CONFIGURED
          ? translate("documentationSync.error.credentialsNotConfigured")
          : errorMessage || translate("integration.error.syncing");
      notify(msg, { type: "error" });
    } finally {
      setStartingSync(false);
    }
  };

  /**
   * Test the saved credentials without writing anything
   */
  const handleTestConnection = async () => {
    clearConnectionCheck();
    const run = connectionTestRun.current;
    setTestingConnection(true);

    try {
      const check = await dataProvider.testIntegrationConnection(organizationId);
      if (run === connectionTestRun.current) {
        setConnectionCheck(check);
      }
    } catch (err) {
      if (run === connectionTestRun.current) {
        notify("integration.error.connectionTest", { type: "error" });
      }
    } finally {
      if (run === connectionTestRun.current) {
        setTestingConnection(false);
      }
    }
  };

  /**
   * Save credentials using real API
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateCredentials();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      // Strip __typename from credentials before sending to mutation
      const { serverUrl, clientId, clientSecret } = integrationState.credentials;
      const credentialsInput: IntegrationCredentialsInput = {
        serverUrl,
        clientId,
        clientSecret,
      };
      const savedSettings = await dataProvider.saveIntegrationSettings(
        organizationId,
        integrationState.provider,
        credentialsInput
      );

      // Update local state with the saved settings to reflect any backend changes
      if (savedSettings?.credentials) {
        setIntegrationState(prev => ({
          ...prev,
          credentials: { ...savedSettings.credentials, clientSecret: "" },
        }));
        setSavedProvider(savedSettings.provider);
        setSavedConnection(toSavedConnection(savedSettings.credentials));
        setHasClientSecret(savedSettings.hasClientSecret);
      }
      clearConnectionCheck();

      notify("integration.success.saved", { type: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      const msg =
        message === INTEGRATION_CREDENTIALS_NOT_CONFIGURED
          ? translate("integration.validation.allFieldsRequired")
          : message === INTEGRATION_SETTINGS_INVALID
            ? translate("integration.validation.invalidUrl")
            : message || translate("integration.error.saving");
      notify(msg, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Get the translation key prefix for the current provider
   */
  const getTranslationPrefix = () => providerTranslations[integrationState.provider];

  if (loading) {
    return (
      <Box sx={{ maxWidth: theme.maxWidth.md, margin: "auto", mt: space.sm }}>
        <Typography>{translate("integration.loading")}</Typography>
      </Box>
    );
  }

  const hasRequiredCredentials = () => {
    return !!(
      integrationState.credentials.serverUrl &&
      integrationState.credentials.clientId &&
      (integrationState.credentials.clientSecret || hasSavedSecret)
    );
  };

  const buttonStyles = {
    flex: 1,
    padding: `${space.sm2}px ${space.xl}px`,
  };

  const isSyncDisabled = saving || startingSync || !hasRequiredCredentials();

  const canTestConnection =
    integrationState.provider === IntegrationProvider.Senso &&
    savedProvider === IntegrationProvider.Senso &&
    hasSavedSecret;
  const formDiffersFromSaved =
    !savedConnection ||
    integrationState.credentials.serverUrl !== savedConnection.serverUrl ||
    integrationState.credentials.clientId !== savedConnection.clientId ||
    !!integrationState.credentials.clientSecret;
  const isTestDisabled = saving || startingSync || testingConnection || formDiffersFromSaved;

  const renderConnectionCheck = (check: IntegrationConnectionCheck) => {
    if (check.ok) {
      const locationCount = check.locationCount ?? 0;
      return translate("integration.connectionTest.success", {
        smart_count: locationCount,
        seconds: (check.durationMs / 1000).toFixed(1),
      });
    }
    const stage = check.stage ? translate(`integration.connectionTest.stage.${check.stage}`) : "";
    const reason = translate(`integration.connectionTest.reason.${check.error ?? "unknown"}`, {
      status: check.httpStatus ?? "",
    });
    // Without a stage the template leaves a space before the colon.
    return translate("integration.connectionTest.failed", { stage, reason }).replace(/\s+:/, ":");
  };

  const translationPrefix = getTranslationPrefix();

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600, margin: "auto", mt: space.sm }}>
        <Typography variant="body2" color="text.secondary" mb={space.sm}>
          {translate("integration.description")}
        </Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel id="integration-provider-label">{translate("integration.form.provider")}</InputLabel>
          <Select
            labelId="integration-provider-label"
            value={integrationState.provider}
            label={translate("integration.form.provider")}
            onChange={handleProviderChange}
          >
            {Object.values(IntegrationProvider).map(provider => (
              <MenuItem key={provider} value={provider}>
                {provider}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={translate(`${translationPrefix}.url`)}
          type="text"
          placeholder={translate(`${translationPrefix}.urlPlaceholder`)}
          fullWidth
          value={integrationState.credentials.serverUrl || ""}
          onChange={handleInputChange("serverUrl")}
          margin="normal"
          required
          error={!!error}
          helperText={translate(`${translationPrefix}.urlHelper`)}
        />

        <TextField
          label={translate(`${translationPrefix}.clientId`)}
          type="text"
          fullWidth
          value={integrationState.credentials.clientId || ""}
          onChange={handleInputChange("clientId")}
          margin="normal"
          required
          error={!!error}
          helperText={translate(`${translationPrefix}.clientIdHelper`)}
        />

        <TextField
          label={translate(`${translationPrefix}.clientSecret`)}
          type="password"
          fullWidth
          value={integrationState.credentials.clientSecret || ""}
          onChange={handleInputChange("clientSecret")}
          margin="normal"
          placeholder={hasSavedSecret ? translate("integration.form.secretSaved") : undefined}
          required={!hasSavedSecret}
          error={!!error}
          helperText={
            hasSavedSecret
              ? translate("integration.form.keepSecret")
              : translate(`${translationPrefix}.clientSecretHelper`)
          }
        />

        {error && (
          <Typography color="error" variant="body2" mt={space.sm}>
            {error}
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: space.xs, mt: space.sm }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={saving || startingSync}
            sx={buttonStyles}
            label={
              saving
                ? "integration.button.saving"
                : translate("integration.button.save", { provider: integrationState.provider })
            }
          />
          <Button
            color="secondary"
            onClick={handleSync}
            disabled={isSyncDisabled}
            sx={buttonStyles}
            startIcon={startingSync ? <Refresh /> : undefined}
            label={startingSync ? "integration.button.starting" : "integration.button.sync"}
          />
          {canTestConnection && (
            <Button
              color="secondary"
              onClick={handleTestConnection}
              disabled={isTestDisabled}
              sx={buttonStyles}
              label={
                testingConnection
                  ? "integration.button.testing"
                  : translate("integration.button.test", { provider: integrationState.provider })
              }
            />
          )}
        </Box>
        {canTestConnection && formDiffersFromSaved && (
          <Typography variant="caption" color="text.secondary" display="block" mt={space.xs}>
            {translate("integration.connectionTest.saveFirst")}
          </Typography>
        )}
        {connectionCheck && (
          <Alert severity={connectionCheck.ok ? "success" : "error"} sx={{ mt: space.sm }}>
            {renderConnectionCheck(connectionCheck)}
          </Alert>
        )}
      </Box>
      {canTestConnection && (
        <IntegrationImportPreview
          organizationId={organizationId}
          disabled={formDiffersFromSaved}
          onImport={handleSync}
          importing={startingSync}
        />
      )}
      <IntegrationExecutionsTable
        executions={executions}
        executionsLoading={executionsLoading}
        onRefresh={loadExecutions}
      />
    </>
  );
};
